import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Shared/Button/Button';
import { RollingText } from '../Shared/RollingText/RollingText';
import { Spinner } from '../Shared/Spinner';
import { RetrainStateEnum } from './types';
import { RefreshIcon, RefreshIconHandle } from '@/Domain/components/icons/RefreshIcon';

type Props = {
  retrainState: RetrainStateEnum | null;
  handleRetrain: () => void;
  /**
   * `true` while the `/retrain` POST is in flight. Disables the button and
   * swaps the icon for a spinner so the user can't fire a second retrain
   * during the gap between click and the websocket-driven `IN_PROGRESS`
   * state arriving — that gap was producing duplicate POSTs that the
   * backend rejected with `/retrain endpoint called while a retrain is in
   * already progress` warnings, leaving newly uploaded articles stuck in
   * `pending` state.
   */
  isPending?: boolean;
};

export const RetrainState = ({ retrainState, handleRetrain, isPending = false }: Props) => {
  const { t } = useTranslation('topbar');
  const refreshIconRef = useRef<RefreshIconHandle>(null);

  if (retrainState === RetrainStateEnum.IN_PROGRESS) {
    return (
      <div className="flex items-center animate-fadeIn min-w-0">
        <Spinner className="mr-2 h-4 w-4 flex-shrink-0 text-foreground" />
        <RollingText
          text={t('retrain_in_progress')}
          className="truncate text-sm text-muted-foreground"
          duration={0.5}
          staggerDelay={0.05}
          loopDelay={1.5}
        />
      </div>
    );
  }

  if (retrainState === RetrainStateEnum.TO_BE_RETRAINED) {
    return (
      <div className="flex items-center animate-fadeIn min-w-0">
        <Button
          variant="custom"
          size="sm"
          className="h-8 flex-shrink-0 border-transparent bg-warning text-white hover:bg-warning/90 disabled:opacity-70"
          onClick={handleRetrain}
          disabled={isPending}
          onMouseEnter={() => refreshIconRef.current?.startAnimation()}
          onMouseLeave={() => refreshIconRef.current?.stopAnimation()}
        >
          {isPending ? (
            <Spinner className="size-4 text-white" />
          ) : (
            <RefreshIcon ref={refreshIconRef} size={18} className="text-white" />
          )}
          {t('button.retrain_ai')}
        </Button>
        <div className="flex items-center min-w-0 overflow-hidden">
          <span className="relative flex h-2 w-2 ml-4 flex-shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-warning"></span>
          </span>
          <span className="ml-3 truncate text-sm text-muted-foreground">
            {t('retrain_is_outdated')}
          </span>
        </div>
      </div>
    );
  }

  return null;
};
